import { useState, useCallback, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid'; // Import uuid
import { LoadingSpinner } from '../LoadingSpinner';
import { useNotifications } from '../../contexts/NotificationContext';
import { motion } from "framer-motion";
import useEscapeKey from '../../hooks/useEscapeKey';
import { useClickOutside } from '../../hooks/useClickOutside';
import ValidatedInput from '../shared/ValidatedInput';
import { DEFAULT_VALIDATION_CONFIG, validateAndSanitizeInput } from '../../utils/validation/inputValidation';
import { UnifiedSchema } from '../../types/schema';
import { createUnifiedSchema } from '../../services/schemaDefinitionApiService';

interface SaveSchemaAsDraftDialogProps {
  open: boolean;
  onClose: () => void;
  currentSchema: UnifiedSchema | null;
  onSaveSuccess: (newSchema: UnifiedSchema) => void;
  existingSchemaNames: string[]; // For duplicate name checking
}

export default function SaveSchemaAsDraftDialog({
  open,
  onClose,
  currentSchema,
  onSaveSuccess,
  existingSchemaNames,
}: SaveSchemaAsDraftDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { showNotification } = useNotifications();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const dialogContentRef = useClickOutside<HTMLDivElement>(onClose);

  useEffect(() => {
    if (open) {
      setName(currentSchema ? currentSchema.name : ''); // Remove (Draft) suffix
      setDescription(''); // Make description empty
      setNameError('');
      setIsLoading(false);
      if (nameInputRef.current) {
        nameInputRef.current.focus();
        nameInputRef.current.select();
      }
    }
  }, [open, currentSchema]);

  const handleSaveAsDraftSubmit = useCallback(async () => {
    if (!currentSchema) {
      showNotification('error', 'No schema to save.');
      return;
    }

    const sanitizedName = validateAndSanitizeInput(name, DEFAULT_VALIDATION_CONFIG.projectName);
    const sanitizedDescription = validateAndSanitizeInput(description, DEFAULT_VALIDATION_CONFIG.projectDescription);

    if (!sanitizedName.trim()) {
      setNameError('Please enter a schema name');
      return;
    }

    if (existingSchemaNames.includes(sanitizedName)) {
      setNameError('A schema with this name already exists. Please choose a different name.');
      return;
    }

    setIsLoading(true);
    try {
      const newSchema: UnifiedSchema = {
        ...currentSchema,
        id: uuidv4(), // Generate new UUID for the unified schema
        name: sanitizedName,
        description: sanitizedDescription,
        events: currentSchema.events.map(event => ({
          ...event,
          isNew: true, // Mark events as new for the new schema
          // parameterFullPaths should be copied as is, as they refer to fullPaths, not the Parameter objects themselves
        })),
        parameters: currentSchema.parameters.map(param => ({ // Map the master list of parameters
          ...param,
          id: uuidv4(), // Generate new UUID for each parameter
        })),
        isNew: true, // Mark the new schema as new
        status: 'draft', // Always save as draft
      };

      const createdSchema = await createUnifiedSchema(newSchema);

      if (createdSchema) {
        showNotification('success', `Schema "${createdSchema.name}" saved as draft.`);
        onSaveSuccess(createdSchema);
        onClose();
      } else {
        showNotification('error', 'Failed to save schema as draft.');
      }
    } catch (error) {
      console.error('Error saving schema as draft:', error);
      showNotification('error', 'Failed to save schema as draft.');
    } finally {
      setIsLoading(false);
    }
  }, [name, description, currentSchema, onSaveSuccess, onClose, showNotification, existingSchemaNames]);

  useEscapeKey(onClose);

  if (!open) {
    return null;
  }

  return (
    <motion.div
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        ref={dialogContentRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[480px] mx-4 border border-gray-200 dark:border-gray-700"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (name.trim() && !nameError) {
              handleSaveAsDraftSubmit();
            }
          }
        }}
      >
        {/* Header */}
        <div className="px-6 pt-5 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Save Schema as Draft</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-4">
            <ValidatedInput
              id="schema-draft-name"
              label="Schema Name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError('');
              }}
              validationConfig={DEFAULT_VALIDATION_CONFIG.projectName}
              placeholder="Enter new schema name"
              errorMessage={nameError}
              onBlur={() => {
                if (!name.trim()) {
                  setNameError('Schema name is required');
                } else if (existingSchemaNames.includes(name)) {
                  setNameError('A schema with this name already exists. Please choose a different name.');
                }
              }}
              ref={nameInputRef}
            />
            <ValidatedInput
              id="schema-draft-description"
              label="Description (Optional)"
              isTextarea={true}
              value={description}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setDescription(e.target.value);
              }}
              validationConfig={DEFAULT_VALIDATION_CONFIG.projectDescription}
              placeholder="Enter a brief description for the new schema"
              rows={3}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 rounded-b-lg border-t border-gray-200 dark:border-gray-800">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAsDraftSubmit}
              disabled={!name.trim() || !!nameError || isLoading}
              className={`
                px-4 py-2 text-sm font-medium rounded inline-flex items-center gap-2
                ${
                  (!name.trim() || !!nameError || isLoading)
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }
              `}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="text-gray-500 dark:text-gray-400" />
                  <span>Saving...</span>
                </>
              ) : (
                'Save as Draft'
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
